/**
 * /auth/accept-invite — Page d'acceptation d'invitation (premier login).
 *
 * Flow :
 *  1. Admin invite via /v1/users/invite ou /v1/brokers/invite
 *  2. Supabase envoie un email avec lien → user clique
 *  3. Supabase redirige ici avec hash `#access_token=...&refresh_token=...&type=invite`
 *  4. parseAuthCallbackUrl() extrait les tokens
 *  5. supabase.auth.setSession() établit la session
 *  6. Le user saisit son premier password (≥12 chars, ≠ email)
 *  7. supabase.auth.updateUser({password}) — le password ne quitte JAMAIS le navigateur
 *  8. Redirect / (user désormais connecté avec son nouveau password)
 *
 * Décision Chunk 7.C : full_name READ-ONLY.
 *  - Source de vérité = invitation admin (déjà dans app.users.full_name + auth metadata)
 *  - L'invité ne peut pas réécrire son identité ici
 *  - Modification possible plus tard via Settings/Profile (Chunk 7.D)
 *
 * Sécurité : identique à ResetConfirmPage (cf. en-tête).
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import { parseAuthCallbackUrl } from "../../lib/auth-callback";
import { authErrorToI18nKey } from "../../lib/auth-error-map";
import { supabase } from "../../lib/supabase";
import { tenantScope } from "../../lib/tenantScope";
import { toast } from "../../lib/toast";

import { AuthCardLayout } from "./_AuthCardLayout";
import { AuthPasswordForm } from "./_AuthPasswordForm";

type SessionState =
  | { kind: "loading" }
  | { kind: "ready"; email: string; fullName: string | null }
  | { kind: "error"; code: string };

function NewAccountInvitePage({ tenantId }: { tenantId: string | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<SessionState>({ kind: "loading" });
  const [passwordSaved, setPasswordSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      const parsed = parseAuthCallbackUrl(window.location.href, window.location.hash);

      if (parsed.error || parsed.type !== "invite") {
        const code = parsed.error ?? "invalid_link";
        if (!cancelled) setState({ kind: "error", code });
        return;
      }

      const { error: setErr } = await supabase.auth.setSession({
        access_token: parsed.accessToken,
        refresh_token: parsed.refreshToken,
      });

      if (cancelled) return;

      if (setErr) {
        setState({ kind: "error", code: "session_failed" });
        return;
      }

      window.history.replaceState(
        null,
        "",
        window.location.pathname + (tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : ""),
      );

      const { data, error: getErr } = await supabase.auth.getUser();
      if (cancelled) return;

      if (getErr || !data.user?.email) {
        setState({ kind: "error", code: "session_failed" });
        return;
      }

      const fullName = (data.user.user_metadata?.["full_name"] as string | undefined) ?? null;

      setState({
        kind: "ready",
        email: data.user.email,
        fullName,
      });
    }

    void init();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  async function handleSubmit(password: string) {
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        toast.error(t("errors.password.update_failed"));
        return;
      }
      toast.success(t("auth.accept_invite.success"));
      if (tenantId) setPasswordSaved(true);
      else navigate("/", { replace: true });
    } catch {
      toast.error(t("errors.password.update_failed"));
    }
  }

  if (passwordSaved && tenantId) return <ExistingMembershipInvitePage tenantId={tenantId} />;

  if (state.kind === "loading") {
    return (
      <AuthCardLayout title={t("auth.accept_invite.title")}>
        <p className="text-sm text-slate-500">{t("app.loading")}</p>
      </AuthCardLayout>
    );
  }

  if (state.kind === "error") {
    return (
      <AuthCardLayout title={t("auth.accept_invite.title")}>
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700"
        >
          {t(authErrorToI18nKey(state.code))}
        </div>
        <div className="mt-4 text-center text-sm">
          <Link to="/auth/login" className="text-brand-600 hover:underline">
            {t("errors.session.back_to_login")}
          </Link>
        </div>
      </AuthCardLayout>
    );
  }

  return (
    <AuthCardLayout
      title={t("auth.accept_invite.title")}
      subtitle={t("auth.accept_invite.subtitle", { email: state.email })}
    >
      {state.fullName && (
        <p className="mb-4 text-sm text-slate-600">
          <span className="font-medium">{t("auth.profile.full_name")} :</span> {state.fullName}
        </p>
      )}

      <AuthPasswordForm
        email={state.email}
        submitLabel={t("auth.accept_invite.submit")}
        submittingLabel={t("app.loading")}
        onSubmit={handleSubmit}
      />

      <div className="mt-6 text-center text-sm">
        <Link to="/auth/login" className="text-brand-600 hover:underline">
          ← {t("common.back")}
        </Link>
      </div>
    </AuthCardLayout>
  );
}

function ExistingMembershipInvitePage({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading, contextError, refresh, switchTenant } = useAuth();
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    if (!loading && user) {
      if (tenantScope() !== tenantId) {
        setBlocked(!switchTenant(tenantId));
        return;
      }
      navigate("/", { replace: true });
    }
  }, [loading, user, tenantId, switchTenant, navigate]);
  if (blocked)
    return (
      <AuthCardLayout title={t("membership.title")}>
        <p role="status">{t("membership.wait_write")}</p>
        <button
          type="button"
          onClick={() => {
            if (switchTenant(tenantId)) setBlocked(false);
          }}
          className="min-h-11 rounded-md px-4 focus-visible:ring-2"
        >
          {t("membership.retry")}
        </button>
      </AuthCardLayout>
    );
  if (contextError)
    return (
      <AuthCardLayout title={t("membership.context_failed")}>
        <button
          type="button"
          onClick={() => void refresh()}
          className="min-h-11 px-4 focus-visible:ring-2"
        >
          {t("membership.retry")}
        </button>
      </AuthCardLayout>
    );
  if (!loading && !user)
    return <Navigate to={`/auth/login?tenant_id=${encodeURIComponent(tenantId)}`} replace />;
  return (
    <AuthCardLayout title={t("membership.title")}>
      <p role="status">{t("app.loading")}</p>
    </AuthCardLayout>
  );
}
export function AcceptInvitePage() {
  const [invitation] = useState(() => {
    const target = new URLSearchParams(window.location.search).get("tenant_id");
    return {
      target:
        target && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(target)
          ? target
          : null,
      callback: Boolean(window.location.hash),
    };
  });
  const target = invitation.target;
  return target && !invitation.callback ? (
    <ExistingMembershipInvitePage tenantId={target} />
  ) : (
    <NewAccountInvitePage tenantId={target} />
  );
}
